// Firestore data access layer for notes (web version)
import { db } from './firebase-config.js';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    deleteDoc,
    addDoc,
    updateDoc,
    query,
    where,
    orderBy,
    onSnapshot,
    serverTimestamp,
    writeBatch,
    arrayUnion,
    arrayRemove
} from 'firebase/firestore';
import { deriveKey, encryptNote, decryptNote } from './encryption.js';

let encryptionKey = null;

/**
 * Initialize encryption key for current user
 * @param {string} userId - Firebase user ID
 */
async function initEncryption(userId) {
    encryptionKey = await deriveKey(userId);
}

/**
 * Generate note ID from key (URL or domain)
 * @param {string} userId - Firebase user ID
 * @param {string} key - URL or domain
 * @returns {string} Firestore document ID
 */
function generateNoteId(userId, key) {
    const sanitizedKey = btoa(key).replace(/[^a-zA-Z0-9]/g, '');
    return `${userId}_${sanitizedKey}`;
}

// Special suffix for metadata documents
const META_SUFFIX = '#__meta__';

/**
 * Save or update a note
 * @param {string} userId - Firebase user ID
 * @param {string} key - URL or domain
 * @param {string} content - Note content (plain text)
 * @param {Array<string>} tags - Optional array of AI-generated tags
 * @param {Object} metadata - Optional metadata { folderId, tagIds }
 * @returns {Promise<void>}
 */
export async function saveNote(userId, key, content, tags = null, metadata = null) {
    if (!encryptionKey) {
        await initEncryption(userId);
    }

    const encrypted = await encryptNote(content, encryptionKey);

    let domain;
    try {
        if (key.startsWith('http')) {
            domain = new URL(key).hostname;
        } else {
            domain = key;
        }
    } catch (e) {
        domain = 'unknown';
    }

    const noteId = generateNoteId(userId, key);
    const noteRef = doc(db, 'notes', noteId);

    const noteData = {
        userId,
        key,
        domain,
        isPageNote: key.startsWith('http'),
        encryptedContent: encrypted.encryptedData,
        iv: encrypted.iv,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
    };

    // Add tags if provided (stored unencrypted for filtering)
    if (tags && Array.isArray(tags)) {
        noteData.tags = tags;
    }

    // Add metadata if provided (folderId, tagIds from manual user assignment)
    if (metadata) {
        if (metadata.folderId !== undefined) {
            noteData.folderId = metadata.folderId;
        }
        if (metadata.tagIds && Array.isArray(metadata.tagIds)) {
            noteData.tagIds = metadata.tagIds;
        }
    }

    await setDoc(noteRef, noteData, { merge: true });
}

/**
 * Update only the tags field of a note
 * @param {string} userId - Firebase user ID
 * @param {string} key - URL or domain
 * @param {Array<string>} tags - Array of tag strings
 * @returns {Promise<void>}
 */
export async function updateNoteTags(userId, key, tags) {
    const noteId = generateNoteId(userId, key);
    const noteRef = doc(db, 'notes', noteId);

    await setDoc(noteRef, {
        tags: tags || [],
        updatedAt: serverTimestamp()
    }, { merge: true });
}

/**
 * Get a single note by key
 * @param {string} userId - Firebase user ID
 * @param {string} key - URL or domain
 * @returns {Promise<string|null>} Decrypted note content or null
 */
export async function getNote(userId, key) {
    if (!encryptionKey) {
        await initEncryption(userId);
    }

    const noteId = generateNoteId(userId, key);
    const noteRef = doc(db, 'notes', noteId);
    const noteSnap = await getDoc(noteRef);

    if (!noteSnap.exists()) {
        return null;
    }

    const data = noteSnap.data();

    const decrypted = await decryptNote({
        encryptedData: data.encryptedContent,
        iv: data.iv
    }, encryptionKey);

    return decrypted;
}

/**
 * Get all notes for a user
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Array>} Array of note objects with decrypted content
 */
export async function getAllNotes(userId) {
    if (!encryptionKey) {
        await initEncryption(userId);
    }

    const notesQuery = query(
        collection(db, 'notes'),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
    );

    const querySnapshot = await getDocs(notesQuery);
    const notes = [];

    for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();

        // Skip metadata documents
        if (data.isMetadata) continue;

        // Skip chrome-extension URLs
        if (data.key && data.key.startsWith('chrome-extension://')) continue;

        try {
            const decrypted = await decryptNote({
                encryptedData: data.encryptedContent,
                iv: data.iv
            }, encryptionKey);

            // Skip notes with restricted content
            if (decrypted && decrypted.includes('Notes are not available for this page')) continue;

            // Include all notes, even empty ones
            // This ensures notes remain visible in the UI when user clears content
            notes.push({
                id: docSnap.id,
                key: data.key,
                content: decrypted || '',
                domain: data.domain,
                isPageNote: data.isPageNote,
                tags: data.tags || [],
                // Organization fields
                folderId: data.folderId || null,
                tagIds: data.tagIds || [],
                isFavorite: data.isFavorite || false,
                isPinned: data.isPinned || false,
                isArchived: data.isArchived || false,
                isDeleted: data.isDeleted || false,
                isShared: data.isShared || false,
                shareId: data.shareId || null,
                updatedAt: data.updatedAt,
                createdAt: data.createdAt
            });
        } catch (error) {
            console.error(`Failed to decrypt note ${docSnap.id}:`, error);
        }
    }

    return notes;
}

/**
 * Delete a note by key
 * @param {string} userId - Firebase user ID
 * @param {string} key - URL or domain
 * @returns {Promise<void>}
 */
export async function deleteNote(userId, key) {
    const baseKey = key.split('#')[0];
    const metaKey = baseKey + META_SUFFIX;
    const metaNoteId = generateNoteId(userId, metaKey);
    const metaRef = doc(db, 'notes', metaNoteId);
    const metaSnap = await getDoc(metaRef);

    const batch = writeBatch(db);

    // Delete the main note (default tab)
    const mainNoteId = generateNoteId(userId, baseKey);
    batch.delete(doc(db, 'notes', mainNoteId));

    // If metadata exists, delete all tab notes and metadata
    if (metaSnap.exists()) {
        try {
            if (!encryptionKey) {
                await initEncryption(userId);
            }
            const data = metaSnap.data();
            const decrypted = await decryptNote({
                encryptedData: data.encryptedContent,
                iv: data.iv
            }, encryptionKey);
            const metadata = JSON.parse(decrypted);

            // Delete each tab's note
            if (metadata.tabs && Array.isArray(metadata.tabs)) {
                for (const tab of metadata.tabs) {
                    if (tab.id !== 'default') {
                        const tabKey = `${baseKey}#${tab.id}`;
                        const tabNoteId = generateNoteId(userId, tabKey);
                        batch.delete(doc(db, 'notes', tabNoteId));
                    }
                }
            }
        } catch (e) {
            console.error('Error parsing metadata during delete:', e);
        }

        batch.delete(metaRef);
    }

    await batch.commit();
}

/**
 * Delete all notes for a user
 * @param {string} userId - Firebase user ID
 * @returns {Promise<number>} Number of deleted notes
 */
export async function deleteAllNotes(userId) {
    const notesQuery = query(
        collection(db, 'notes'),
        where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(notesQuery);

    if (querySnapshot.empty) {
        return 0;
    }

    // Delete in batches of 500 (Firestore limit)
    const batchSize = 500;
    let deletedCount = 0;
    let currentBatch = writeBatch(db);
    let operationsInBatch = 0;

    for (const docSnap of querySnapshot.docs) {
        currentBatch.delete(docSnap.ref);
        operationsInBatch++;
        deletedCount++;

        if (operationsInBatch >= batchSize) {
            await currentBatch.commit();
            currentBatch = writeBatch(db);
            operationsInBatch = 0;
        }
    }

    // Commit remaining operations
    if (operationsInBatch > 0) {
        await currentBatch.commit();
    }

    return deletedCount;
}

/**
 * Subscribe to real-time updates for all user notes
 * @param {string} userId - Firebase user ID
 * @param {Function} callback - Called with array of notes on updates
 * @returns {Function} Unsubscribe function
 */
export function subscribeToNotes(userId, callback) {
    const notesQuery = query(
        collection(db, 'notes'),
        where('userId', '==', userId)
    );

    return onSnapshot(notesQuery, async (snapshot) => {
        if (!encryptionKey) {
            await initEncryption(userId);
        }

        const notes = [];
        for (const docSnap of snapshot.docs) {
            const data = docSnap.data();

            // Skip metadata and chrome-extension URLs
            if (data.isMetadata) continue;
            if (data.key && data.key.startsWith('chrome-extension://')) continue;

            try {
                const decrypted = await decryptNote({
                    encryptedData: data.encryptedContent,
                    iv: data.iv
                }, encryptionKey);

                // Skip restricted content
                if (decrypted && decrypted.includes('Notes are not available for this page')) continue;

                // Include all notes, even empty ones
                notes.push({
                    id: docSnap.id,
                    key: data.key,
                    content: decrypted || '',
                    domain: data.domain,
                    isPageNote: data.isPageNote,
                    tags: data.tags || [],
                    // Organization fields
                    folderId: data.folderId || null,
                    tagIds: data.tagIds || [],
                    isFavorite: data.isFavorite || false,
                    isPinned: data.isPinned || false,
                    isArchived: data.isArchived || false,
                    isDeleted: data.isDeleted || false,
                    isShared: data.isShared || false,
                    shareId: data.shareId || null,
                    updatedAt: data.updatedAt,
                    createdAt: data.createdAt
                });
            } catch (error) {
                console.error(`Failed to decrypt note:`, error);
            }
        }

        callback(notes);
    });
}

/**
 * Get page metadata (tabs list and last active tab)
 * @param {string} userId - Firebase user ID
 * @param {string} baseKey - Normalized URL or domain
 * @returns {Promise<Object>} Metadata object with tabs array and lastActiveTabId
 */
export async function getPageMetadata(userId, baseKey) {
    if (!encryptionKey) {
        await initEncryption(userId);
    }

    const metaKey = baseKey + META_SUFFIX;
    const noteId = generateNoteId(userId, metaKey);
    const noteRef = doc(db, 'notes', noteId);
    const noteSnap = await getDoc(noteRef);

    if (!noteSnap.exists()) {
        return {
            tabs: [{ id: 'default', title: 'Note 1' }],
            lastActiveTabId: 'default'
        };
    }

    const data = noteSnap.data();

    try {
        const decrypted = await decryptNote({
            encryptedData: data.encryptedContent,
            iv: data.iv
        }, encryptionKey);

        return JSON.parse(decrypted);
    } catch (error) {
        console.error('Failed to decrypt page metadata:', error);
        return {
            tabs: [{ id: 'default', title: 'Note 1' }],
            lastActiveTabId: 'default'
        };
    }
}

// ============================================
// FOLDER MANAGEMENT FUNCTIONS
// ============================================

/**
 * Create a new folder
 * @param {string} userId - Firebase user ID
 * @param {Object} folderData - Folder data { name, parentId?, color?, icon?, order? }
 * @returns {Promise<Object>} Created folder with ID
 */
export async function createFolder(userId, folderData) {
    const foldersRef = collection(db, 'users', userId, 'folders');
    
    const folder = {
        name: folderData.name,
        parentId: folderData.parentId || null,
        color: folderData.color || null,
        icon: folderData.icon || null,
        order: folderData.order ?? 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(foldersRef, folder);
    
    return {
        id: docRef.id,
        ...folder
    };
}

/**
 * Update folder properties
 * @param {string} userId - Firebase user ID
 * @param {string} folderId - Folder ID to update
 * @param {Object} updates - Fields to update { name?, parentId?, color?, icon?, order? }
 * @returns {Promise<void>}
 */
export async function updateFolder(userId, folderId, updates) {
    const folderRef = doc(db, 'users', userId, 'folders', folderId);
    
    const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
    };
    
    // Remove undefined values
    Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
            delete updateData[key];
        }
    });
    
    await updateDoc(folderRef, updateData);
}

/**
 * Delete a folder and optionally delete or move notes
 * @param {string} userId - Firebase user ID
 * @param {string} folderId - Folder ID to delete
 * @param {boolean} deleteNotes - If true, delete notes in folder; if false, move to root
 * @returns {Promise<{deletedFolder: string, movedNotes: number, deletedNotes: number}>}
 */
export async function deleteFolder(userId, folderId, deleteNotes = false) {
    const batch = writeBatch(db);
    
    // Delete the folder
    const folderRef = doc(db, 'users', userId, 'folders', folderId);
    batch.delete(folderRef);
    
    // Find all notes in this folder
    const notesQuery = query(
        collection(db, 'notes'),
        where('userId', '==', userId),
        where('folderId', '==', folderId)
    );
    
    const notesSnapshot = await getDocs(notesQuery);
    let movedNotes = 0;
    let deletedNotes = 0;
    
    for (const noteDoc of notesSnapshot.docs) {
        if (deleteNotes) {
            batch.delete(noteDoc.ref);
            deletedNotes++;
        } else {
            batch.update(noteDoc.ref, {
                folderId: null,
                updatedAt: serverTimestamp()
            });
            movedNotes++;
        }
    }
    
    // Handle nested folders - move them to root
    const nestedFoldersQuery = query(
        collection(db, 'users', userId, 'folders'),
        where('parentId', '==', folderId)
    );
    
    const nestedFoldersSnapshot = await getDocs(nestedFoldersQuery);
    for (const nestedFolderDoc of nestedFoldersSnapshot.docs) {
        batch.update(nestedFolderDoc.ref, {
            parentId: null,
            updatedAt: serverTimestamp()
        });
    }
    
    await batch.commit();
    
    return { deletedFolder: folderId, movedNotes, deletedNotes };
}

/**
 * Get all folders for a user
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Array>} Array of folder objects
 */
export async function getFolders(userId) {
    const foldersQuery = query(
        collection(db, 'users', userId, 'folders'),
        orderBy('order', 'asc')
    );
    
    const snapshot = await getDocs(foldersQuery);
    
    return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
    }));
}

/**
 * Get a single folder by ID
 * @param {string} userId - Firebase user ID
 * @param {string} folderId - Folder ID
 * @returns {Promise<Object|null>} Folder object or null if not found
 */
export async function getFolder(userId, folderId) {
    const folderRef = doc(db, 'users', userId, 'folders', folderId);
    const folderSnap = await getDoc(folderRef);
    
    if (!folderSnap.exists()) {
        return null;
    }
    
    return {
        id: folderSnap.id,
        ...folderSnap.data()
    };
}

/**
 * Update folder order (for drag-drop reordering)
 * @param {string} userId - Firebase user ID
 * @param {string[]} orderedFolderIds - Array of folder IDs in desired order
 * @returns {Promise<void>}
 */
export async function updateFolderOrder(userId, orderedFolderIds) {
    const batch = writeBatch(db);
    
    orderedFolderIds.forEach((folderId, index) => {
        const folderRef = doc(db, 'users', userId, 'folders', folderId);
        batch.update(folderRef, {
            order: index,
            updatedAt: serverTimestamp()
        });
    });
    
    await batch.commit();
}

/**
 * Move a note to a folder
 * @param {string} userId - Firebase user ID
 * @param {string} noteId - Note document ID
 * @param {string|null} folderId - Target folder ID (null for root/unfiled)
 * @returns {Promise<void>}
 */
export async function moveNoteToFolder(userId, noteId, folderId) {
    const noteRef = doc(db, 'notes', noteId);
    
    // Verify the note belongs to the user
    const noteSnap = await getDoc(noteRef);
    if (!noteSnap.exists() || noteSnap.data().userId !== userId) {
        throw new Error('Note not found or access denied');
    }
    
    await updateDoc(noteRef, {
        folderId: folderId,
        updatedAt: serverTimestamp()
    });
}

// ============================================
// TAG MANAGEMENT FUNCTIONS
// ============================================

/**
 * Create a new tag
 * @param {string} userId - Firebase user ID
 * @param {Object} tagData - Tag data { name, color? }
 * @returns {Promise<Object>} Created tag with ID
 */
export async function createTag(userId, tagData) {
    const tagsRef = collection(db, 'users', userId, 'tags');
    
    const tag = {
        name: tagData.name.toLowerCase().trim(),
        color: tagData.color || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(tagsRef, tag);
    
    return {
        id: docRef.id,
        ...tag
    };
}

/**
 * Update tag properties
 * @param {string} userId - Firebase user ID
 * @param {string} tagId - Tag ID to update
 * @param {Object} updates - Fields to update { name?, color? }
 * @returns {Promise<void>}
 */
export async function updateTag(userId, tagId, updates) {
    const tagRef = doc(db, 'users', userId, 'tags', tagId);
    
    const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
    };
    
    if (updateData.name) {
        updateData.name = updateData.name.toLowerCase().trim();
    }
    
    // Remove undefined values
    Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
            delete updateData[key];
        }
    });
    
    await updateDoc(tagRef, updateData);
}

/**
 * Delete a tag and remove it from all notes
 * @param {string} userId - Firebase user ID
 * @param {string} tagId - Tag ID to delete
 * @returns {Promise<{deletedTag: string, affectedNotes: number}>}
 */
export async function deleteTag(userId, tagId) {
    const batch = writeBatch(db);
    
    // Delete the tag
    const tagRef = doc(db, 'users', userId, 'tags', tagId);
    batch.delete(tagRef);
    
    // Find all notes with this tag and remove it
    const notesQuery = query(
        collection(db, 'notes'),
        where('userId', '==', userId),
        where('tagIds', 'array-contains', tagId)
    );
    
    const notesSnapshot = await getDocs(notesQuery);
    let affectedNotes = 0;
    
    for (const noteDoc of notesSnapshot.docs) {
        batch.update(noteDoc.ref, {
            tagIds: arrayRemove(tagId),
            updatedAt: serverTimestamp()
        });
        affectedNotes++;
    }
    
    await batch.commit();
    
    return { deletedTag: tagId, affectedNotes };
}

/**
 * Get all tags for a user
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Array>} Array of tag objects
 */
export async function getTags(userId) {
    const tagsQuery = query(
        collection(db, 'users', userId, 'tags'),
        orderBy('name', 'asc')
    );
    
    const snapshot = await getDocs(tagsQuery);
    
    return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
    }));
}

/**
 * Get a single tag by ID
 * @param {string} userId - Firebase user ID
 * @param {string} tagId - Tag ID
 * @returns {Promise<Object|null>} Tag object or null if not found
 */
export async function getTag(userId, tagId) {
    const tagRef = doc(db, 'users', userId, 'tags', tagId);
    const tagSnap = await getDoc(tagRef);
    
    if (!tagSnap.exists()) {
        return null;
    }
    
    return {
        id: tagSnap.id,
        ...tagSnap.data()
    };
}

/**
 * Add a tag to a note
 * @param {string} userId - Firebase user ID
 * @param {string} noteId - Note document ID
 * @param {string} tagId - Tag ID to add
 * @returns {Promise<void>}
 */
export async function addTagToNote(userId, noteId, tagId) {
    const noteRef = doc(db, 'notes', noteId);
    
    // Verify the note belongs to the user
    const noteSnap = await getDoc(noteRef);
    if (!noteSnap.exists() || noteSnap.data().userId !== userId) {
        throw new Error('Note not found or access denied');
    }
    
    await updateDoc(noteRef, {
        tagIds: arrayUnion(tagId),
        updatedAt: serverTimestamp()
    });
}

/**
 * Remove a tag from a note
 * @param {string} userId - Firebase user ID
 * @param {string} noteId - Note document ID
 * @param {string} tagId - Tag ID to remove
 * @returns {Promise<void>}
 */
export async function removeTagFromNote(userId, noteId, tagId) {
    const noteRef = doc(db, 'notes', noteId);
    
    // Verify the note belongs to the user
    const noteSnap = await getDoc(noteRef);
    if (!noteSnap.exists() || noteSnap.data().userId !== userId) {
        throw new Error('Note not found or access denied');
    }
    
    await updateDoc(noteRef, {
        tagIds: arrayRemove(tagId),
        updatedAt: serverTimestamp()
    });
}

/**
 * Set all tags for a note (replaces existing tags)
 * @param {string} userId - Firebase user ID
 * @param {string} noteId - Note document ID
 * @param {string[]} tagIds - Array of tag IDs to set
 * @returns {Promise<void>}
 */
export async function setNoteTags(userId, noteId, tagIds) {
    const noteRef = doc(db, 'notes', noteId);
    
    // Verify the note belongs to the user
    const noteSnap = await getDoc(noteRef);
    if (!noteSnap.exists() || noteSnap.data().userId !== userId) {
        throw new Error('Note not found or access denied');
    }
    
    await updateDoc(noteRef, {
        tagIds: tagIds || [],
        updatedAt: serverTimestamp()
    });
}

// ============================================
// FAVORITES & ORGANIZATION FUNCTIONS
// ============================================

/**
 * Toggle or set favorite status of a note
 * @param {string} userId - Firebase user ID
 * @param {string} noteId - Note document ID
 * @param {boolean} [isFavorite] - Optional: explicit favorite status. If omitted, toggles current status.
 * @returns {Promise<boolean>} New favorite status
 */
export async function toggleNoteFavorite(userId, noteId, isFavorite) {
    const noteRef = doc(db, 'notes', noteId);
    
    const noteSnap = await getDoc(noteRef);
    if (!noteSnap.exists() || noteSnap.data().userId !== userId) {
        throw new Error('Note not found or access denied');
    }
    
    const currentStatus = noteSnap.data().isFavorite || false;
    const newStatus = isFavorite !== undefined ? isFavorite : !currentStatus;
    
    await updateDoc(noteRef, {
        isFavorite: newStatus,
        updatedAt: serverTimestamp()
    });
    
    return newStatus;
}

/**
 * Toggle or set pinned status of a note
 * @param {string} userId - Firebase user ID
 * @param {string} noteId - Note document ID
 * @param {boolean} [isPinned] - Optional: explicit pinned status. If omitted, toggles current status.
 * @returns {Promise<boolean>} New pinned status
 */
export async function toggleNotePinned(userId, noteId, isPinned) {
    const noteRef = doc(db, 'notes', noteId);
    
    const noteSnap = await getDoc(noteRef);
    if (!noteSnap.exists() || noteSnap.data().userId !== userId) {
        throw new Error('Note not found or access denied');
    }
    
    const currentStatus = noteSnap.data().isPinned || false;
    const newStatus = isPinned !== undefined ? isPinned : !currentStatus;
    
    await updateDoc(noteRef, {
        isPinned: newStatus,
        pinnedAt: newStatus ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
    });
    
    return newStatus;
}

/**
 * Toggle or set archived status of a note
 * @param {string} userId - Firebase user ID
 * @param {string} noteId - Note document ID
 * @param {boolean} [isArchived] - Optional: explicit archived status. If omitted, toggles current status.
 * @returns {Promise<boolean>} New archived status
 */
export async function toggleNoteArchived(userId, noteId, isArchived) {
    const noteRef = doc(db, 'notes', noteId);
    
    const noteSnap = await getDoc(noteRef);
    if (!noteSnap.exists() || noteSnap.data().userId !== userId) {
        throw new Error('Note not found or access denied');
    }
    
    const currentStatus = noteSnap.data().isArchived || false;
    const newStatus = isArchived !== undefined ? isArchived : !currentStatus;
    
    await updateDoc(noteRef, {
        isArchived: newStatus,
        archivedAt: newStatus ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
    });
    
    return newStatus;
}

/**
 * Soft delete a note (move to trash)
 * @param {string} userId - Firebase user ID
 * @param {string} noteId - Note document ID
 * @returns {Promise<void>}
 */
export async function softDeleteNote(userId, noteId) {
    const noteRef = doc(db, 'notes', noteId);
    
    const noteSnap = await getDoc(noteRef);
    if (!noteSnap.exists() || noteSnap.data().userId !== userId) {
        throw new Error('Note not found or access denied');
    }
    
    await updateDoc(noteRef, {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    });
}

/**
 * Restore a note from trash
 * @param {string} userId - Firebase user ID
 * @param {string} noteId - Note document ID
 * @returns {Promise<void>}
 */
export async function restoreNote(userId, noteId) {
    const noteRef = doc(db, 'notes', noteId);
    
    const noteSnap = await getDoc(noteRef);
    if (!noteSnap.exists() || noteSnap.data().userId !== userId) {
        throw new Error('Note not found or access denied');
    }
    
    await updateDoc(noteRef, {
        isDeleted: false,
        deletedAt: null,
        updatedAt: serverTimestamp()
    });
}

/**
 * Permanently delete a note (hard delete)
 * @param {string} userId - Firebase user ID
 * @param {string} noteId - Note document ID
 * @returns {Promise<void>}
 */
export async function permanentlyDeleteNote(userId, noteId) {
    const noteRef = doc(db, 'notes', noteId);
    
    const noteSnap = await getDoc(noteRef);
    if (!noteSnap.exists() || noteSnap.data().userId !== userId) {
        throw new Error('Note not found or access denied');
    }
    
    await deleteDoc(noteRef);
}

// ============================================
// BULK OPERATIONS
// ============================================

/**
 * Bulk move notes to a folder
 * @param {string} userId - Firebase user ID
 * @param {string[]} noteIds - Array of note document IDs
 * @param {string|null} folderId - Target folder ID (null for root)
 * @returns {Promise<{moved: number, failed: number}>}
 */
export async function bulkMoveToFolder(userId, noteIds, folderId) {
    const batch = writeBatch(db);
    let moved = 0;
    let failed = 0;
    
    for (const noteId of noteIds) {
        try {
            const noteRef = doc(db, 'notes', noteId);
            const noteSnap = await getDoc(noteRef);
            
            if (noteSnap.exists() && noteSnap.data().userId === userId) {
                batch.update(noteRef, {
                    folderId: folderId,
                    updatedAt: serverTimestamp()
                });
                moved++;
            } else {
                failed++;
            }
        } catch (error) {
            console.error(`Failed to prepare move for note ${noteId}:`, error);
            failed++;
        }
    }
    
    if (moved > 0) {
        await batch.commit();
    }
    
    return { moved, failed };
}

/**
 * Bulk add tag to notes
 * @param {string} userId - Firebase user ID
 * @param {string[]} noteIds - Array of note document IDs
 * @param {string} tagId - Tag ID to add
 * @returns {Promise<{updated: number, failed: number}>}
 */
export async function bulkAddTag(userId, noteIds, tagId) {
    const batch = writeBatch(db);
    let updated = 0;
    let failed = 0;
    
    for (const noteId of noteIds) {
        try {
            const noteRef = doc(db, 'notes', noteId);
            const noteSnap = await getDoc(noteRef);
            
            if (noteSnap.exists() && noteSnap.data().userId === userId) {
                batch.update(noteRef, {
                    tagIds: arrayUnion(tagId),
                    updatedAt: serverTimestamp()
                });
                updated++;
            } else {
                failed++;
            }
        } catch (error) {
            console.error(`Failed to prepare tag add for note ${noteId}:`, error);
            failed++;
        }
    }
    
    if (updated > 0) {
        await batch.commit();
    }
    
    return { updated, failed };
}

/**
 * Bulk remove tag from notes
 * @param {string} userId - Firebase user ID
 * @param {string[]} noteIds - Array of note document IDs
 * @param {string} tagId - Tag ID to remove
 * @returns {Promise<{updated: number, failed: number}>}
 */
export async function bulkRemoveTag(userId, noteIds, tagId) {
    const batch = writeBatch(db);
    let updated = 0;
    let failed = 0;
    
    for (const noteId of noteIds) {
        try {
            const noteRef = doc(db, 'notes', noteId);
            const noteSnap = await getDoc(noteRef);
            
            if (noteSnap.exists() && noteSnap.data().userId === userId) {
                batch.update(noteRef, {
                    tagIds: arrayRemove(tagId),
                    updatedAt: serverTimestamp()
                });
                updated++;
            } else {
                failed++;
            }
        } catch (error) {
            console.error(`Failed to prepare tag remove for note ${noteId}:`, error);
            failed++;
        }
    }
    
    if (updated > 0) {
        await batch.commit();
    }
    
    return { updated, failed };
}

/**
 * Bulk favorite/unfavorite notes
 * @param {string} userId - Firebase user ID
 * @param {string[]} noteIds - Array of note document IDs
 * @param {boolean} isFavorite - Favorite status to set
 * @returns {Promise<{updated: number, failed: number}>}
 */
export async function bulkFavorite(userId, noteIds, isFavorite) {
    const batch = writeBatch(db);
    let updated = 0;
    let failed = 0;
    
    for (const noteId of noteIds) {
        try {
            const noteRef = doc(db, 'notes', noteId);
            const noteSnap = await getDoc(noteRef);
            
            if (noteSnap.exists() && noteSnap.data().userId === userId) {
                batch.update(noteRef, {
                    isFavorite: isFavorite,
                    updatedAt: serverTimestamp()
                });
                updated++;
            } else {
                failed++;
            }
        } catch (error) {
            console.error(`Failed to prepare favorite for note ${noteId}:`, error);
            failed++;
        }
    }
    
    if (updated > 0) {
        await batch.commit();
    }
    
    return { updated, failed };
}

/**
 * Bulk archive notes
 * @param {string} userId - Firebase user ID
 * @param {string[]} noteIds - Array of note document IDs
 * @returns {Promise<{archived: number, failed: number}>}
 */
export async function bulkArchive(userId, noteIds) {
    const batch = writeBatch(db);
    let archived = 0;
    let failed = 0;
    
    for (const noteId of noteIds) {
        try {
            const noteRef = doc(db, 'notes', noteId);
            const noteSnap = await getDoc(noteRef);
            
            if (noteSnap.exists() && noteSnap.data().userId === userId) {
                batch.update(noteRef, {
                    isArchived: true,
                    archivedAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
                archived++;
            } else {
                failed++;
            }
        } catch (error) {
            console.error(`Failed to prepare archive for note ${noteId}:`, error);
            failed++;
        }
    }
    
    if (archived > 0) {
        await batch.commit();
    }
    
    return { archived, failed };
}

/**
 * Bulk soft delete notes (move to trash)
 * @param {string} userId - Firebase user ID
 * @param {string[]} noteIds - Array of note document IDs
 * @returns {Promise<{deleted: number, failed: number}>}
 */
export async function bulkDelete(userId, noteIds) {
    const batch = writeBatch(db);
    let deleted = 0;
    let failed = 0;
    
    for (const noteId of noteIds) {
        try {
            const noteRef = doc(db, 'notes', noteId);
            const noteSnap = await getDoc(noteRef);
            
            if (noteSnap.exists() && noteSnap.data().userId === userId) {
                batch.update(noteRef, {
                    isDeleted: true,
                    deletedAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
                deleted++;
            } else {
                failed++;
            }
        } catch (error) {
            console.error(`Failed to prepare delete for note ${noteId}:`, error);
            failed++;
        }
    }
    
    if (deleted > 0) {
        await batch.commit();
    }
    
    return { deleted, failed };
}

/**
 * Bulk permanently delete notes (hard delete)
 * @param {string} userId - Firebase user ID
 * @param {string[]} noteIds - Array of note document IDs
 * @returns {Promise<{deleted: number, failed: number}>}
 */
export async function bulkPermanentDelete(userId, noteIds) {
    const batch = writeBatch(db);
    let deleted = 0;
    let failed = 0;
    
    for (const noteId of noteIds) {
        try {
            const noteRef = doc(db, 'notes', noteId);
            const noteSnap = await getDoc(noteRef);
            
            if (noteSnap.exists() && noteSnap.data().userId === userId) {
                batch.delete(noteRef);
                deleted++;
            } else {
                failed++;
            }
        } catch (error) {
            console.error(`Failed to prepare permanent delete for note ${noteId}:`, error);
            failed++;
        }
    }
    
    if (deleted > 0) {
        await batch.commit();
    }
    
    return { deleted, failed };
}

// ============================================
// QUERY FUNCTIONS
// ============================================

/**
 * Get notes in a specific folder
 * @param {string} userId - Firebase user ID
 * @param {string|null} folderId - Folder ID (null for unfiled/root notes)
 * @returns {Promise<Array>} Array of notes with decrypted content
 */
export async function getNotesByFolder(userId, folderId) {
    if (!encryptionKey) {
        await initEncryption(userId);
    }
    
    const notesQuery = query(
        collection(db, 'notes'),
        where('userId', '==', userId),
        where('folderId', '==', folderId),
        where('isArchived', '==', false),
        where('isDeleted', '==', false),
        orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(notesQuery);
    const notes = [];
    
    for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        
        // Skip metadata documents
        if (data.isMetadata) continue;
        
        try {
            const decrypted = await decryptNote({
                encryptedData: data.encryptedContent,
                iv: data.iv
            }, encryptionKey);
            
            notes.push({
                id: docSnap.id,
                key: data.key,
                content: decrypted || '',
                domain: data.domain,
                isPageNote: data.isPageNote,
                folderId: data.folderId || null,
                tagIds: data.tagIds || [],
                tags: data.tags || [],
                isFavorite: data.isFavorite || false,
                isPinned: data.isPinned || false,
                isShared: data.isShared || false,
                shareId: data.shareId || null,
                updatedAt: data.updatedAt,
                createdAt: data.createdAt
            });
        } catch (error) {
            console.error(`Failed to decrypt note ${docSnap.id}:`, error);
        }
    }
    
    return notes;
}

/**
 * Get notes with a specific tag
 * @param {string} userId - Firebase user ID
 * @param {string} tagId - Tag ID to filter by
 * @returns {Promise<Array>} Array of notes with decrypted content
 */
export async function getNotesByTag(userId, tagId) {
    if (!encryptionKey) {
        await initEncryption(userId);
    }
    
    const notesQuery = query(
        collection(db, 'notes'),
        where('userId', '==', userId),
        where('tagIds', 'array-contains', tagId),
        where('isArchived', '==', false),
        where('isDeleted', '==', false),
        orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(notesQuery);
    const notes = [];
    
    for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        
        // Skip metadata documents
        if (data.isMetadata) continue;
        
        try {
            const decrypted = await decryptNote({
                encryptedData: data.encryptedContent,
                iv: data.iv
            }, encryptionKey);
            
            notes.push({
                id: docSnap.id,
                key: data.key,
                content: decrypted || '',
                domain: data.domain,
                isPageNote: data.isPageNote,
                folderId: data.folderId || null,
                tagIds: data.tagIds || [],
                tags: data.tags || [],
                isFavorite: data.isFavorite || false,
                isPinned: data.isPinned || false,
                isShared: data.isShared || false,
                shareId: data.shareId || null,
                updatedAt: data.updatedAt,
                createdAt: data.createdAt
            });
        } catch (error) {
            console.error(`Failed to decrypt note ${docSnap.id}:`, error);
        }
    }
    
    return notes;
}

/**
 * Get all archived notes for a user
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Array>} Array of archived notes with decrypted content
 */
export async function getArchivedNotes(userId) {
    if (!encryptionKey) {
        await initEncryption(userId);
    }
    
    const notesQuery = query(
        collection(db, 'notes'),
        where('userId', '==', userId),
        where('isArchived', '==', true),
        where('isDeleted', '==', false),
        orderBy('archivedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(notesQuery);
    const notes = [];
    
    for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        
        // Skip metadata documents
        if (data.isMetadata) continue;
        
        try {
            const decrypted = await decryptNote({
                encryptedData: data.encryptedContent,
                iv: data.iv
            }, encryptionKey);
            
            notes.push({
                id: docSnap.id,
                key: data.key,
                content: decrypted || '',
                domain: data.domain,
                isPageNote: data.isPageNote,
                folderId: data.folderId || null,
                tagIds: data.tagIds || [],
                tags: data.tags || [],
                isFavorite: data.isFavorite || false,
                isPinned: data.isPinned || false,
                isShared: data.isShared || false,
                shareId: data.shareId || null,
                isArchived: true,
                archivedAt: data.archivedAt,
                updatedAt: data.updatedAt,
                createdAt: data.createdAt
            });
        } catch (error) {
            console.error(`Failed to decrypt archived note ${docSnap.id}:`, error);
        }
    }
    
    return notes;
}

/**
 * Get all deleted notes (trash) for a user
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Array>} Array of deleted notes with decrypted content
 */
export async function getDeletedNotes(userId) {
    if (!encryptionKey) {
        await initEncryption(userId);
    }
    
    const notesQuery = query(
        collection(db, 'notes'),
        where('userId', '==', userId),
        where('isDeleted', '==', true),
        orderBy('deletedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(notesQuery);
    const notes = [];
    
    for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        
        // Skip metadata documents
        if (data.isMetadata) continue;
        
        try {
            const decrypted = await decryptNote({
                encryptedData: data.encryptedContent,
                iv: data.iv
            }, encryptionKey);
            
            notes.push({
                id: docSnap.id,
                key: data.key,
                content: decrypted || '',
                domain: data.domain,
                isPageNote: data.isPageNote,
                folderId: data.folderId || null,
                tagIds: data.tagIds || [],
                tags: data.tags || [],
                isDeleted: true,
                deletedAt: data.deletedAt,
                updatedAt: data.updatedAt,
                createdAt: data.createdAt
            });
        } catch (error) {
            console.error(`Failed to decrypt deleted note ${docSnap.id}:`, error);
        }
    }
    
    return notes;
}

/**
 * Get all favorite notes for a user
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Array>} Array of favorite notes with decrypted content
 */
export async function getFavoriteNotes(userId) {
    if (!encryptionKey) {
        await initEncryption(userId);
    }
    
    const notesQuery = query(
        collection(db, 'notes'),
        where('userId', '==', userId),
        where('isFavorite', '==', true),
        where('isArchived', '==', false),
        where('isDeleted', '==', false),
        orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(notesQuery);
    const notes = [];
    
    for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        
        // Skip metadata documents
        if (data.isMetadata) continue;
        
        try {
            const decrypted = await decryptNote({
                encryptedData: data.encryptedContent,
                iv: data.iv
            }, encryptionKey);
            
            notes.push({
                id: docSnap.id,
                key: data.key,
                content: decrypted || '',
                domain: data.domain,
                isPageNote: data.isPageNote,
                folderId: data.folderId || null,
                tagIds: data.tagIds || [],
                tags: data.tags || [],
                isFavorite: true,
                isPinned: data.isPinned || false,
                isShared: data.isShared || false,
                shareId: data.shareId || null,
                updatedAt: data.updatedAt,
                createdAt: data.createdAt
            });
        } catch (error) {
            console.error(`Failed to decrypt favorite note ${docSnap.id}:`, error);
        }
    }
    
    return notes;
}

export { initEncryption };
